import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import Box from 'ui-box';
import {
  Button,
  FormField,
  InputGroup,
  majorScale,
  useToast,
} from '../../npaper';
import { useStores } from '../../stores';
import validateEmail from '../../utils/email-validator';
import { AuthLayout, AuthTitle } from './AuthLayout';

export const AuthForgot = () => {
  const intl = useIntl();
  const history = useHistory();

  const { authStore } = useStores();
  const toaster = useToast();

  const { register, handleSubmit, errors, formState, setError } =
    useForm<{
      email: string;
    }>();

  const [success, setSuccess] = useState(false);

  const onSubmit = async (values: { email: string }) => {
    try {
      await authStore.requestPasswordReset(values.email);
      setSuccess(true);
    } catch (error) {
      if (error.response && error.response.data) {
        const emailError = error.response.data.email;
        if (emailError && emailError.length === 1) {
          setError('email', {
            message: emailError[0],
          });
          return;
        }
      }
      toaster.showDefaultError(error);
    }
  };

  return (
    <AuthLayout>
      <AuthTitle
        title={
          success
            ? intl.formatMessage({
                defaultMessage: 'Instructions sent!',
              })
            : intl.formatMessage({ defaultMessage: 'Reset your password' })
        }
        sub={intl.formatMessage({ defaultMessage: 'Forgot your password?' })}
      />

      {success && (
        <>
          <p className="mb-4">
            <FormattedMessage defaultMessage="Perfect! An email with instructions should land in your inbox soon." />
          </p>
          <p className="mb-4">
            <FormattedMessage defaultMessage="If you haven't received an email after a while, please contact support." />
          </p>
        </>
      )}

      {!success && (
        <FormField
          label={intl.formatMessage({
            defaultMessage: 'Email',
            description: 'Form field label',
          })}
          marginBottom={majorScale(2)}
          className="medium"
        >
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Email',
              description: 'Input placeholder',
            })}
            name="email"
            isInvalid={!!errors.email}
            validationMessage={errors.email && errors.email.message}
            ref={register({
              validate: (v: string) =>
                (!validateEmail(v) &&
                  intl.formatMessage({
                    defaultMessage: 'Please provide a valid email address',
                  })) ||
                true,
              required: intl.formatMessage({
                defaultMessage:
                  'We need to know your email to help you reset your password',
              }),
            })}
          />
        </FormField>
      )}

      <Box display="flex" flexWrap="wrap" justifyContent="space-between">
        <Button
          minimal
          iconLeft="arrow-left"
          onClick={() => history.replace('/auth')}
          disabled={formState.isSubmitting}
        >
          <FormattedMessage defaultMessage="Back" />
        </Button>
        {!success && (
          <Button
            primary
            minWidth="50%"
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            <FormattedMessage
              defaultMessage="Send instructions"
              description="Primary button text - to reset password"
            />
          </Button>
        )}
      </Box>
    </AuthLayout>
  );
};
